import React, { useEffect, ReactNode } from "react";
import Konva from "konva";
import { Html } from "react-konva-utils";
import {
  useFloating,
  offset,
  useDismiss,
  useRole,
  useInteractions,
  Placement,
  useTransitionStyles,
  FloatingFocusManager,
} from "@floating-ui/react";
import { useKonvaVirtualElement } from "frontend/ui-components/design-system/tooltip/use-konva-virtual-element";

type KonvaPickerProps = {
  konvaNode: Konva.Node | null;
  stage: Konva.Stage | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buttonRef: React.RefObject<Konva.Node>;
  side?: Placement;
  children: ReactNode;
  className?: string;
  role?: "listbox" | "dialog";
};

/**
 * A reusable picker component for Konva canvas elements.
 * Handles positioning, dismiss behavior, and transitions.
 * Similar to KonvaTooltip but for interactive pickers.
 */
export function KonvaPicker({
  konvaNode,
  stage,
  open,
  onOpenChange,
  buttonRef,
  side = "bottom-start",
  children,
  className,
  role: roleType = "dialog",
}: KonvaPickerProps) {
  const virtualElement = useKonvaVirtualElement(konvaNode, stage);

  const { refs, floatingStyles, context } = useFloating({
    open: open && !!konvaNode,
    onOpenChange,
    placement: side,
    transform: false,
    middleware: [offset(6)],
  });

  // Set the virtual element as the reference
  useEffect(() => {
    if (open && konvaNode && stage) {
      refs.setReference(virtualElement);
    }
  }, [refs, open, konvaNode, stage, virtualElement]);

  const dismiss = useDismiss(context, {
    outsidePress: (event) => {
      if (event.target instanceof HTMLCanvasElement && stage && buttonRef.current) {
        const pointerPos = stage.getPointerPosition();
        if (!pointerPos) return true;

        const buttonRect = buttonRef.current.getClientRect();
        const pointerRect = { x: pointerPos.x, y: pointerPos.y, width: 1, height: 1 };
        const isOnButton = Konva.Util.haveIntersection(buttonRect, pointerRect);

        return !isOnButton;
      }

      return true;
    },
  });
  const role = useRole(context, { role: roleType });

  const { getFloatingProps } = useInteractions([dismiss, role]);

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 150,
    initial: {
      opacity: 0,
      transform: "scale(0.95)",
    },
    common: ({ side }) => ({
      transformOrigin: {
        top: "bottom",
        bottom: "top",
        left: "right",
        right: "left",
      }[side],
    }),
  });

  if (!konvaNode || !stage || !isMounted) {
    return null;
  }

  const pickerContent = (
    <FloatingFocusManager context={context} modal={false}>
      <div
        ref={refs.setFloating}
        style={{
          ...floatingStyles,
          ...transitionStyles,
        }}
        className={className}
        {...getFloatingProps()}
      >
        {children}
      </div>
    </FloatingFocusManager>
  );

  return <Html>{pickerContent}</Html>;
}
